<template>
  <div class="site-header-wrapper">
    <div
      ref="container"
      class="site-header-container"
      :class="{top}"
    >
      <div
        v-if="showAnnouncement && announcementText"
        class="site-header__announcement text-body-xs"
        v-text="announcementText"
      />
      <header
        ref="siteHeader"
        class="site-header"
        data-sign-post-priority="1"
        :js-sign-post="$string('accessibility.sign_posts.site_header')"
      >
        <div class="site-header__masthead">
          <div class="site-header__container">
            <!-- Menu icon -->
            <button
              class="site-header__menu icon-button"
              type="button"
              :aria-label="$string('general.site_header.menu')"
              @click="openDrawer"
            >
              <span
                class="site-header__hamburger hamburger"
                :class="{ [$class('active')]: getActiveOverlay === 'menuDrawer' }"
              >
                <icon-menu v-if="getActiveOverlay !== 'menuDrawer'" />
                <icon-close v-else />
              </span>
            </button>
            <!-- Navigation -->
            <nav class="site-header__navigation">
              <ul class="site-header__navigation-list">
                <li
                  v-for="(tier1) of menu.items"
                  :key="tier1.handle"
                  class="site-header__navigation-item"
                  @mouseenter="handleNavOpen(tier1)"
                  @mouseleave="handleNavClose"
                >
                  <a
                    class="site-header__navigation-link text-body-s"
                    :data-text="tier1.title"
                    :class="{
                      'text-body-s-bold': meganav.open && meganav.currentMenu?.title === tier1.title
                    }"
                    :style="linkStyle(tier1.title)"
                    :href="menuLinkUrl(tier1.url)"
                  >
                    {{ tier1.title }}
                    <span
                      v-show="meganav.open && meganav.currentMenu?.title === tier1.title"
                      class="site-header__menu-badge"
                      aria-hidden="true"
                    />
                  </a>
                </li>
              </ul>
            </nav>
            <!-- Logo -->
            <a
              class="site-header__logo"
              :href="$variable('routes.root')"
            >
              <Transition
                v-show="!top"
                name="logo-slide"
                mode="out-in"
              >
                <span
                  v-if="!top"
                  key="name"
                  class="site-header__name-logo"
                >
                  <img
                    v-if="nameLogo"
                    :src="nameLogo.src"
                    :alt="nameLogo.alt"
                    class="site-header__logo-image"
                  />
                </span>
              </Transition>
              <Transition
                v-show="top"
                name="logo-slide"
                mode="out-in"
              >
                <span
                  v-if="top"
                  key="main"
                  class="site-header__v-logo"
                >
                  <img
                    v-if="mainLogo"
                    :src="mainLogo.src"
                    :alt="mainLogo.alt"
                    class="site-header__logo-image"
                  />
                </span>
              </Transition>
            </a>
            <!-- Icons -->
            <div class="site-header__icons">
              <button
                v-if="showSearchIcon"
                type="button"
                class="site-header__search"
                :aria-label="$string('search.title')"
                @click="openSearch"
              >
                <icon-search />
              </button>

              <a
                v-if="showAccountIcon"
                :href="$variable('customer.loggedIn') ?
                  $variable('routes.account.url') :
                  $variable('routes.account.login')"
                class="site-header__account icon-button"
                :aria-label="$variable('customer.loggedIn') ?
                  $string('general.site_header.account') :
                  $string('general.site_header.login')"
              >
                <icon-account />
              </a>

              <button
                v-if="showCartIcon"
                type="button"
                class="site-header__cart icon-button"
                :aria-label="$string('general.site_header.cart')"
                @click="handleCartToggle"
              >
                <icon-cart />

                <span
                  v-if="cart.item_count > 0"
                  class="site-header__cart-badge"
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        </div>
      </header>

      <!-- Search -->
      <overlay
        v-if="!isMobile"
        key="searchShelf"
        block-class="search-shelf"
        direction="fade"
        namespace="searchShelf"
        :template="false"
        type="shelf"
        show-overlay
      >
        <template #body="{ isActive }">
          <search-native
            v-if="true"
            :vip-customer="vipCustomer"
            :is-active="isActive"
          />
        </template>
      </overlay>

      <!-- Meganav -->
      <overlay
        key="megaNav"
        block-class="mega-nav-overlay"
        direction="fade"
        namespace="megaNav"
        :template="false"
        type="shelf"
      >
        <template #body="{ isActive }">
          <meganav
            v-if="getComponentVisible('mega-nav')"
            :is-active="isActive"
            :menu-blocks="activeBlocks"
            :menu="meganav.currentMenu"
            :link-styles="linkStyles"
            @mouseenter="handleNavEnter"
            @mouseleave="handleNavExit"
          />
        </template>
      </overlay>

      <!-- Menu drawer -->
      <overlay
        key="menuDrawer"
        block-class="menu-drawer-overlay"
        direction="fade"
        namespace="menuDrawer"
        :template="false"
        type="drawer"
        :show-overlay="false"
      >
        <template #body="{ isActive }">
          <menu-drawer
            v-if="getComponentVisible('menu-drawer')"
            v-cloak
            :handle="mobile.handle"
            :is-active="isActive"
            :item-count="mobile.itemCount"
            :menu-blocks="menuBlocks"
            :link-styles="linkStyles"
            :mobile-menu-blocks="mobileMenuBlocks"
            :show-search-icon="showSearchIcon"
            :show-account-icon="showAccountIcon"
          />
        </template>
      </overlay>
    </div>
  </div>
</template>

<script>

/**
 * Component / Global: Site header
 * -----------------------------------------------------------------------------
 * Site header component, contains menu drawer and meganav.
 * - No style import as it's included in critical stylesheet.
 *
 * @param {Object} desktop - Desktop linklist handle and items.
 * @param {Object} mobile - Mobile linklist handle and item count.
 *
 */
import { defineAsyncComponent, nextTick } from 'vue'
import { mapActions, mapGetters, mapState } from 'vuex'

import Overlay from '~global/overlay/overlay'

import IconCart from '~icons/misc/cart.svg'
import IconMenu from '~icons/misc/Menu.svg'
import IconSearch from '~icons/misc/search.svg'
import IconAccount from '~icons/misc/account.svg'
import IconClose from '~icons/misc/close.svg'

import SearchNative from '~async/search-native/search-native'
import menu from '~/graphql/queries/menu.gql'
import { formatMenu } from '~/helpers/format'
import timings from '~/config/timings'

export default {
  name: 'SiteHeader',

  components: {
    IconCart,
    IconMenu,
    IconSearch,
    IconAccount,
    IconClose,
    MenuDrawer: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.menu-drawer' */
        '~async/menu-drawer/menu-drawer'
      ),
    }),
    Meganav: defineAsyncComponent({
      loader: () => import(
        /* webpackChunkName: 'component.meganav' */
        '~global/mega-nav/mega-nav'
      ),
    }),
    Overlay,
    SearchNative,
  },

  props: {
    mainLogo: {
      type: Object,
      default: () => null,
    },
    nameLogo: {
      type: Object,
      default: () => null,
    },
    desktop: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        items: [],
      }),
    },
    mobile: {
      type: Object,
      default: () => ({
        handle: 'main-menu',
        itemCount: 5,
      }),
    },
    announcementText: {
      type: String,
      default: '',
    },
    showAnnouncement: {
      type: Boolean,
      default: true,
    },
    menuBlocks: {
      type: Array,
      default: () => [],
    },
    mobileMenuBlocks: {
      type: Array,
      default: () => [],
    },
    vipCustomer: {
      type: Boolean,
      default: false,
    },
    linkStyles: {
      type: Object,
      default: () => ({}),
    },
    showSearchIcon: {
      type: Boolean,
      default: true,
    },
    showAccountIcon: {
      type: Boolean,
      default: true,
    },
    showCartIcon: {
      type: Boolean,
      default: true,
    },
  },

  data() {
    return {
      height: {
        total: 0,
        visible: 0,
      },
      top: false,
      menu: this.desktop,
      navTimeout: null,
      meganav: {
        currentMenu: null,
        open: false,
      },
    }
  },

  computed: {

    /**
     * Map Vuex getters.
     */
    ...mapGetters({
      getActiveOverlay: 'overlays/getActiveOverlay',
      getComponentExists: 'components/getExists',
    }),

    /**
     * Map Vuex state.
     */
    ...mapState({
      cart: (state) => state.cart.response,
      headerState: (state) => state.index.scroll.header,
      isMobile: (state) => state.index.screen.mobile,
      loadedStylesheets: (state) => state.index.stylesheets,
    }),

    /**
     * Filters the menuBlocks down and grabs the ones that have the same
     * title as the currently hovered section to make sure we only render the
     * correct blocks on each menu item
     */
    activeBlocks() {
      const activeMenu = this.meganav.currentMenu

      return this.menuBlocks.filter(block => {
        return block.menu_item === activeMenu.title
      })
    },
  },

  watch: {

    /**
     * Watch header state to update height variables.
     * - Wait for site header transition to end before updating height.
     * - Set height immediately if prefers reduced motion.
     */
    headerState() {
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.setHeights()
        return
      }

      this.$refs.siteHeader.ontransitionend = (args) => {
        if (!args || args.propertyName !== 'transform') {
          return
        }

        this.setHeights()
      }
    },

    /**
     * Watch announcement bar state to update header height.
     */
    'showAnnouncement'(newValue) {
      const isAnnouncement = newValue === true || newValue === 'true';
      if (!isAnnouncement) {
        document.documentElement.style.setProperty(
          '--header-fixed-height',
          'var(--header-height-no-announcement)'
        )
        this.$refs.siteHeader.style.marginBottom = '32px'
      } else {
        document.documentElement.style.setProperty(
          '--header-fixed-height',
          '92px'
        )
        this.$refs.siteHeader.style.marginBottom = '0'
      }
    },

    /**
     * Watch total height to update CSS variables.
     */
    'height.total'() {
      document.documentElement.style.setProperty(
        '--header-height',
        `${this.height.total}px`,
      )
    },

    /**
     * Watch visible height to update CSS variable.
     */
    'height.visible'() {
      document.documentElement.style.setProperty(
        '--header-visible-height',
        `${this.height.visible}px`,
      )
    },

    /**
     * Watch loaded stylesheets to set height.
     */
    loadedStylesheets() {
      if (!this.loadedStylesheets.includes('layout.theme.css')) {
        return
      }

      /**
       * Delay set height to wait for CSS to be parsed.
       */
      setTimeout(() => {
        this.setHeights()
      }, 200)
    },
  },

  mounted() {
    this.queryMenu()

    const isAnnouncement = this.showAnnouncement === true || this.showAnnouncement === 'true';
    if (isAnnouncement) {
      document.documentElement.style.setProperty(
        '--header-fixed-height',
        '92px'
      )
      this.$refs.siteHeader.style.marginBottom = '0'
    } else {
      document.documentElement.style.setProperty(
        '--header-fixed-height',
        'var(--header-height-no-announcement)'
      )
      this.$refs.siteHeader.style.marginBottom = '32px'
    }

    nextTick().then(() => {
      const observerElement = document.querySelector(
        '#vue-element [header-js="observer"]',
      )

      const options = {
        rootMargin: '0px',
        threshold: 1.0,
      }

      const observer = new IntersectionObserver((entries) => {
        const { y } = observerElement.getBoundingClientRect()

        if (y > window.pageYOffset) {
          this.top = false
          return
        }

        this.top = !entries[0].isIntersecting
      }, options)

      observer.observe(observerElement)
      this.setHeights(true)

      const target = document.querySelector('.search-shelf')
      const header = document.querySelector('.site-header-container')
      const nav = document.querySelector('.site-header')

      const observerSearch = new MutationObserver((mutationsList) => {
        for (const mutation of mutationsList) {
          if (mutation.type === 'attributes' && mutation.attributeName === 'style' && !target.style.display) {
            header.style.zIndex = 'var(--layer-temporary)'
            nav.style.zIndex = 'var(--layer-temporary)'
          } else if (mutation.type === 'attributes' && mutation.attributeName === 'style' && target.style.display === 'none') {
            header.style.removeProperty('z-index')
            nav.style.removeProperty('z-index')
          }
        }
      })

      observerSearch.observe(target, { attributes: true })
    })
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      toggleOverlay: 'overlays/toggle',
      openOverlay: 'overlays/open',
      closeOverlay: 'overlays/close',
      setSearchActive: 'search/setSearchActive',
    }),

    /**
     * Adds link styling from metaobject
     * @param {String} link - the link that's being transformed
     */
    linkStyle(link) {
      if (!this.linkStyles[link]) {
        return {}
      }

      return {
        color: this.linkStyles[link] || '',
      }
    },

    /**
     * Quick and dirty fix so that we don't get CORS issues in the page
     * will need a better fix in the future
     * @param {String} link - the link that's being transformed
     */
    menuLinkUrl(link) {
      const newLink = link.replace(cnvs.store.permanentDomain, cnvs.store.shopDomain)
      return newLink
    },

    /**
     * Handles the action when the nav closes
     */
    handleNavClose() {
      this.navTimeout = setTimeout(() => {
        this.meganav.open = false
        this.closeOverlay()
      }, timings.normal)
    },

    /**
     * Handles opening the nav and setting the appropriate tier
     * @param {Object} tier the tier that is opening on the nav
     */
    handleNavOpen(tier) {
      if (tier.links.length === 0) {
        return
      }

      clearTimeout(this.navTimeout)

      this.setHeaderBottom()

      this.meganav.currentMenu = tier
      this.meganav.open = true
      this.openOverlay({
        component: 'mega-nav',
        ignoreDismissed: true,
        namespace: 'megaNav',
      })
    },

    /**
     * Handles the mouse enter event on the nav
     */
    handleNavEnter() {
      clearTimeout(this.navTimeout)
    },

    /**
     * Handles the mouse leave event on the nav
     */
    handleNavExit() {
      this.navTimeout = setTimeout(() => {
        this.meganav.open = false
        this.closeOverlay()
      }, timings.normal)
    },

    /**
     * Sets the position of the bottom of the header as this can move due to
     * the splash section
     */
    setHeaderBottom() {
      const header = document.querySelector('.site-header')
      const { height, y } = header.getBoundingClientRect()

      document.documentElement.style.setProperty(
        '--header-bottom',
        `${height + y}px`,
      )
    },

    /**
     * Moved Open Drawer into it's own function so that we
     * can calculate the position of the drawer as the splash section
     * makes the usual css variables unreliable so I set a new one here on open
     */
    async openDrawer() {
      const containerTop = this.$refs.container.getBoundingClientRect()?.top

      if (containerTop > 0) {
        await new Promise((resolve) => {
          window.scrollTo({
            top: containerTop + document.documentElement.scrollTop,
            behavior: 'smooth',
          })

          /**
           * When scrolling has stopped, resolve and continue.
           * - In place to delay drawer opening until scrolling is complete.
           */
          let scrollTimer = false

          window.addEventListener('scroll', () => {
            window.clearTimeout(scrollTimer)

            scrollTimer = setTimeout(() => {
              resolve()
            }, 66)

          }, false)
        })
      }

      const header = this.$refs.siteHeader
      const headerHeight = header.offsetHeight
      const headerTop = header.getBoundingClientRect()?.top

      document.documentElement.style.setProperty(
        '--menu-drawer-top',
        `${headerHeight + headerTop}px`,
      )

      if (this.getActiveOverlay === 'menuDrawer') {
        this.setSearchActive(false)
      }

      this.toggleOverlay({
        component: 'menu-drawer',
        ignoreDismissed: true,
        namespace: 'menuDrawer',
      })
    },

    /**
     * Opens the search overlay
     */
    openSearch() {
      const rect = this.$refs.container.getBoundingClientRect()
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop
      const targetScrollTop = rect.top + scrollTop

      window.scroll({
        top: targetScrollTop,
        behavior: 'smooth',
      })

      setTimeout(() => {
        this.toggleOverlay({
          namespace: 'searchShelf',
          ignoreDismissed: true,
        })
      }, timings.normal)
    },

    /**
     * Queries the menu to grab it from SF API so we have all of the child tiers
     * so we can pass these to the mega-nav
     */
    queryMenu() {
      return new Promise(async(resolve, reject) => {
        if (!this.desktop.handle) {
          resolve()
          return
        }

        try {
          const response = await cnvs.Query({
            query: menu,
            variables: {
              handle: this.desktop.handle,
              language: window.cnvs.store.language,
              country: window.cnvs.store.country,
            },
          })

          const formattedMenu = formatMenu({ response })

          this.menu.items = formattedMenu
        } catch (error) {
          reportError('Failed to load menu', error)
          reject(error)
        }
      })
    },

    /**
     * Set heights of fixed header.
     * - Height.total is used for body padding.
     * - Height.visible is used for overlays etc.
     * - Remove setHeights if header is not fixed.
     * @param {Boolean} mounted - If height is being set on mounted.
     */
    setHeights(mounted) {
      if (mounted) {
        this.height.total = this.$refs.siteHeader.offsetHeight
      }

      /**
       * Visible height is element height combined with translateY value.
       */
      this.height.visible =
        this.$refs.siteHeader.offsetHeight +
        this.$refs.siteHeader.getBoundingClientRect()?.y
    },

    /**
     * Handle cart toggle.
     * - Prevents opening cart drawer on cart page.
     * @param {Object} event - Click event.
     */
    handleCartToggle(event) {
      if (
        this.$variable('page.type') === 'cart' ||
        !this.getComponentExists('cart-drawer')
      ) {
        return
      }

      event.preventDefault()

      this.toggleOverlay({
        component: 'cart-drawer',
        ignoreDismissed: true,
        namespace: 'cartDrawer',
      })
    },
  },
}
</script>
